import {
  WORDING_CATALOG,
  mergeFieldLabel,
  type MergeFieldName,
  type Wording,
  type WordingKind,
} from '@servwell/shared';

/**
 * The edit-wording sheet's pure half (MAP 249 ticket 05): its field chips, and where a chip's
 * field goes. The words themselves are never made here: the server renders the preview with the
 * send's own function (`emailWording.preview`), and refuses what can't be saved.
 */

export type WordingPart = keyof Wording;

/** Where the owner was last: the part, and its selection (a caret is an empty one). */
export interface Caret {
  part: WordingPart;
  start: number;
  end: number;
}

/** How a field is written in a wording. */
export const fieldToken = (name: MergeFieldName): string => `{{${name}}}`;

/** One chip: the field it adds, and what it stands for in this kind. */
export interface FieldChip {
  name: MergeFieldName;
  token: string;
  label: string;
}

/** The kind's own fields, in the catalog's order: no chip offers a field the save would refuse. */
export function chipsFor(kind: WordingKind): FieldChip[] {
  return WORDING_CATALOG[kind].fields.map((name) => ({
    name,
    token: fieldToken(name),
    label: mergeFieldLabel(kind, name),
  }));
}

/** Where a chip goes when the owner has not been in either part yet: the end of the message. */
export const endOfMessage = (wording: Wording): Caret => ({
  part: 'body',
  start: wording.body.length,
  end: wording.body.length,
});

/**
 * `{{name}}` put into `wording` where the owner was, in place of anything selected, and the caret
 * just after it. A caret outside its text (the text changed under it) is held to the text's ends.
 */
export function insertField(
  wording: Wording,
  caret: Caret,
  name: MergeFieldName,
): { wording: Wording; caret: Caret } {
  const text = wording[caret.part];
  const start = Math.min(Math.max(caret.start, 0), text.length);
  const end = Math.min(Math.max(caret.end, start), text.length);
  const token = fieldToken(name);
  const at = start + token.length;
  return {
    wording: { ...wording, [caret.part]: text.slice(0, start) + token + text.slice(end) },
    caret: { part: caret.part, start: at, end: at },
  };
}

/** Characters as the server counts them (by code point), for the counters under the fields. */
export const charCount = (s: string): number => Array.from(s).length;

/** Whose email the preview shows, in a line. */
export function exampleLine(example: { sample: boolean; label: string }): string {
  return example.sample
    ? 'A sample, until there is a real one to show.'
    : `Your latest: ${example.label}.`;
}
